'use client'

import { IconInfoCircleFill } from '@macarongfactory/web-icons'
import { Divider, InfoBox, Label, Text } from '@macarongfactory/web-ui'
import { Fragment, useRef, useState } from 'react'

import { ExpandFooter } from '@/entities/place'
import {
  applyCouponDiscount,
  COUPON_APPLICABLE_SECTIONS,
} from '@/entities/reservation'
import { logEvent } from '@/shared/external/firebase/analytics'
import { CODE, EVENT_NAMES, LABELS, TYPE } from '@/shared/lib'
import { OptimizedImage } from '@/shared/ui'

import {
  CAR_WASH_SECTION,
  ENGINE_OIL_SECTION,
  type IPlaceCategoryProduct,
  PLACE_CATEGORY_PRODUCTS,
  PLACE_PRODUCT_LIST_INITIAL_COUNT,
  TIRE_SECTION,
} from '../../lib'
import { parsePrice } from '../../lib'
import { useImpressionLog } from '../../model'
import { type IProductGroup } from '../../model'

interface IPlaceProductListProps {
  productGroups: IProductGroup[]
}

const INFO_TEXT =
  '앱으로 예약/결제시 적용되는 가격으로 차종 및 유종별로 다를 수 있어요.'

// 할인율(원가 대비)을 쿠폰적용가 기준으로 계산하는 섹션(엔진오일·타이어·세차). 그 외는 판매가 기준 (Figma 스펙)
const DISCOUNT_RATE_ON_COUPON_PRICE_SECTIONS = new Set<string>([
  ENGINE_OIL_SECTION,
  TIRE_SECTION,
  CAR_WASH_SECTION,
])

const SECTION_IMPRESSION_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.PRODUCT_PRICE,
  type: TYPE.SECTION,
  label: LABELS.COMMON_WEB_PLACE.PRODUCT_PRICE,
}
const EXPAND_BUTTON_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.PRODUCT_PRICE,
  type: TYPE.BUTTON,
  label: LABELS.COMMON_WEB_PLACE.EXPAND,
}

const PlaceProductList = ({ productGroups }: IPlaceProductListProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const sectionRef = useRef<HTMLElement | null>(null)
  const expandButtonRef = useRef<HTMLDivElement | null>(null)
  useImpressionLog({
    ref: sectionRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.SECTION.SHOW,
    params: SECTION_IMPRESSION_PARAMS,
  })
  const productGroupBySection = new Map<string, IProductGroup>()
  productGroups.forEach(group => {
    if (group.section) productGroupBySection.set(group.section, group)
  })

  const matchedCategories = PLACE_CATEGORY_PRODUCTS.filter(category =>
    productGroupBySection.has(category.section)
  )

  const hasMore = matchedCategories.length > PLACE_PRODUCT_LIST_INITIAL_COUNT
  useImpressionLog({
    ref: expandButtonRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.ITEM.SHOW,
    params: EXPAND_BUTTON_PARAMS,
    enabled: hasMore && !isExpanded,
  })

  if (matchedCategories.length === 0) return null

  const visibleCategories = isExpanded
    ? matchedCategories
    : matchedCategories.slice(0, PLACE_PRODUCT_LIST_INITIAL_COUNT)

  const handleExpandClick = () => {
    if (!isExpanded) {
      logEvent(EVENT_NAMES.COMMON_WEB_PLACE.ITEM.CLICK, EXPAND_BUTTON_PARAMS)
    }
    setIsExpanded(prev => !prev)
  }

  return (
    <section
      ref={sectionRef}
      aria-label='상품 가격'
      className='flex flex-col gap-dimension-x4 bg-background-surface-default px-dimension-x5 py-dimension-x6'
    >
      <div className='flex items-center justify-between gap-dimension-x2'>
        <div className='flex items-center gap-dimension-x2'>
          <Text
            as='h2'
            variant='title-1-semibold'
            color='text-foreground-neutral-strong'
          >
            상품 가격
          </Text>
          <Label
            text='마이클 전국 동일 정찰제'
            type='weak'
            size='small'
            variant='brand'
          />
        </div>
        <Text
          variant='body-2-medium'
          color='text-foreground-neutral-assistive'
          className='whitespace-nowrap'
        >
          마이클 최저가 기준
        </Text>
      </div>

      <InfoBox
        text={INFO_TEXT}
        icon={<IconInfoCircleFill width={20} height={20} />}
      />

      {visibleCategories.map((category, index) => (
        <Fragment key={category.id}>
          {index > 0 && <Divider.Horizontal />}
          <ProductItem
            category={category}
            productGroup={productGroupBySection.get(category.section)}
          />
        </Fragment>
      ))}

      {hasMore && (
        <div ref={expandButtonRef}>
          <ExpandFooter
            text={isExpanded ? '닫기' : '더보기'}
            chevron={isExpanded ? 'up' : 'down'}
            onClick={handleExpandClick}
          />
        </div>
      )}
    </section>
  )
}

interface IProductItemProps {
  category: IPlaceCategoryProduct
  productGroup: IProductGroup | undefined
}

const ProductItem = ({ category, productGroup }: IProductItemProps) => {
  const lowestProduct = productGroup?.lowestPriceTargetProduct
  const rawSalePrice = parsePrice(lowestProduct?.salePrice) // 판매가격
  // 엔진오일·타이어·세차는 판매가에서 쿠폰액을 차감한 쿠폰적용가로 노출
  const displayPrice = applyCouponDiscount(productGroup?.section, rawSalePrice)
  // 원가격. 세차 등 원가가 비어있는 경우 할인가를 기준선으로 폴백해 쿠폰 할인분이 취소선/할인율로 노출되게 함
  const originalPrice = parsePrice(lowestProduct?.price) || rawSalePrice

  // 할인율 기준: 엔진오일·타이어·세차는 쿠폰적용가, 그 외는 판매가
  const discountBase = DISCOUNT_RATE_ON_COUPON_PRICE_SECTIONS.has(
    productGroup?.section ?? ''
  )
    ? displayPrice
    : rawSalePrice

  const hasSalePrice = rawSalePrice > 0
  const discountRate =
    originalPrice > discountBase
      ? Math.round(((originalPrice - discountBase) / originalPrice) * 100)
      : 0
  const showOriginalPrice = originalPrice > 0 && discountRate > 0

  // 이름·설명 모두 프론트 정의 고정값 사용 (기획 확정 표 기준)
  const displayName = category.name
  const displayDescription = category.description
  // 프론트 고정 이미지 우선, 없으면 API 상품 이미지로 폴백 (예: 맞춤 진단)
  const displayImageUrl =
    category.imageUrl || lowestProduct?.imageUrl || productGroup?.imageUrl

  return (
    <div className='flex w-full items-center gap-dimension-x2'>
      <div className='flex min-w-px flex-1 items-center justify-center gap-dimension-x1-5'>
        <div className='relative size-[56px] shrink-0 overflow-clip'>
          {displayImageUrl && (
            <OptimizedImage
              src={displayImageUrl}
              alt={displayName}
              width='100%'
              height='100%'
              radius={0}
              sizes='56px'
              className='absolute inset-0'
            />
          )}
        </div>
        <div className='flex flex-1 flex-col items-start gap-0.5'>
          <Text
            variant='title-1-bold'
            color='text-foreground-neutral-strong'
            className='whitespace-nowrap'
          >
            {displayName}
          </Text>
          {displayDescription && (
            <Text
              variant='caption-1-medium'
              color='text-foreground-neutral-alternative'
              className='line-clamp-2 min-w-full flex-1 break-keep'
            >
              {displayDescription}
            </Text>
          )}
        </div>
      </div>
      <div className='flex shrink-0 flex-col items-end justify-center whitespace-nowrap text-right'>
        {COUPON_APPLICABLE_SECTIONS.has(category.section) && (
          <Text variant='body-1-semibold' color='text-foreground-brand-strong'>
            쿠폰적용가
          </Text>
        )}
        <Text variant='title-1-semibold' color='text-foreground-neutral-strong'>
          {hasSalePrice ? `${displayPrice.toLocaleString()}원~` : '-'}
        </Text>
        {showOriginalPrice && (
          <div className='flex items-center gap-dimension-x1'>
            {discountRate > 0 && (
              <Text
                variant='title-2-semibold'
                color='text-foreground-brand-strong'
              >
                {discountRate}%
              </Text>
            )}
            <Text
              variant='body-1-medium'
              color='text-foreground-neutral-assistive'
              className='line-through'
            >
              {originalPrice.toLocaleString()}원
            </Text>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlaceProductList
